/** UI-free dispatch from an `AdjustmentConfig` to the matching
 *  `applyXxxToCanvas` math. Lives separately from `registry.ts`
 *  because the registry pulls in React Panel components and we
 *  want the slow-path compositor in `render-pipeline.ts` to stay
 *  free of UI deps (and free of the cost of pulling React into
 *  any non-UI consumer's bundle / test).
 *
 *  Adding an implemented kind = entry in the switch below + entry
 *  in the registry's `ADJUSTMENT_SPECS`. The two stay aligned by
 *  type — `AdjustmentConfig['kind']` is the discriminator both
 *  sides switch on. */
import type { AdjustmentConfig } from '../../types/image-editor.types';
import {
  applyBrightnessContrastToCanvas,
  type BrightnessContrastValue,
} from './brightness-contrast';
import { applyColorBalanceToCanvas, type ColorBalanceValue } from './color-balance';
import { applyCurvesToCanvas, type CurvesValue } from './curves';
import { applyHueSaturationToCanvas, type HueSaturationValue } from './hue-saturation';
import { applyLevelsToCanvas, type LevelsValue } from './levels';
import { applyShadowsHighlightsToCanvas, type ShadowsHighlightsValue } from './shadows-highlights';
import { applyVibranceToCanvas, type VibranceValue } from './vibrance';

/** Apply an adjustment config to a target canvas, reading source
 *  pixels from a captured `ImageData`. Non-cumulative: calling
 *  twice with the same config produces the same pixels — the apply
 *  functions all read from `source` and write to `target` rather
 *  than reading the target.
 *
 *  Returns `true` when the kind matched an implemented dispatch,
 *  `false` for placeholder kinds (`selective-color`, `invert`,
 *  etc.) whose math hasn't shipped yet. Callers should treat a
 *  `false` return as "nothing changed" — leaves the target as-is.
 *  Adjustment-layer creation paths gate on `isImplementedKind`
 *  upfront so a placeholder never reaches this code in production. */
export function applyAdjustmentToCanvas(
  source: ImageData,
  target: HTMLCanvasElement,
  config: AdjustmentConfig,
): boolean {
  switch (config.kind) {
    case 'brightness-contrast':
      applyBrightnessContrastToCanvas(source, target, config);
      return true;
    case 'color-balance':
      applyColorBalanceToCanvas(source, target, config);
      return true;
    case 'curves':
      applyCurvesToCanvas(source, target, config);
      return true;
    case 'hue-saturation':
      applyHueSaturationToCanvas(source, target, config);
      return true;
    case 'levels':
      applyLevelsToCanvas(source, target, config);
      return true;
    case 'shadows-highlights':
      applyShadowsHighlightsToCanvas(source, target, config);
      return true;
    case 'vibrance':
      applyVibranceToCanvas(source, target, config);
      return true;
    default:
      return false;
  }
}

/** Build a typed `AdjustmentConfig` from a kind discriminator and
 *  its matching `*Value`. The per-kind switch lets TypeScript prove
 *  the value's shape matches the kind, replacing the `kind:` +
 *  spread + double-cast pattern callers would otherwise need at
 *  every config-construction site. */
export type AdjustmentValueByKind = {
  'brightness-contrast': BrightnessContrastValue;
  'color-balance': ColorBalanceValue;
  curves: CurvesValue;
  'hue-saturation': HueSaturationValue;
  levels: LevelsValue;
  'shadows-highlights': ShadowsHighlightsValue;
  vibrance: VibranceValue;
};

export type ImplementedAdjustmentKind = keyof AdjustmentValueByKind;

export function buildAdjustmentConfig<K extends ImplementedAdjustmentKind>(
  kind: K,
  value: AdjustmentValueByKind[K],
): AdjustmentConfig {
  switch (kind) {
    case 'brightness-contrast':
      return { kind, ...(value as BrightnessContrastValue) };
    case 'color-balance':
      return { kind, ...(value as ColorBalanceValue) };
    case 'curves':
      return { kind, ...(value as CurvesValue) };
    case 'hue-saturation':
      return { kind, ...(value as HueSaturationValue) };
    case 'levels':
      return { kind, ...(value as LevelsValue) };
    case 'shadows-highlights':
      return { kind, ...(value as ShadowsHighlightsValue) };
    case 'vibrance':
      return { kind, ...(value as VibranceValue) };
  }
  // Exhaustiveness — unreachable if `K extends ImplementedAdjustmentKind`.
  const _exhaustive: never = kind;
  return _exhaustive;
}

/** True when the config's kind has a runtime dispatch in
 *  `applyAdjustmentToCanvas`. Picker / creation paths should gate
 *  on this so a placeholder kind never lands as a real
 *  adjustment-layer config. */
export function isImplementedKind(kind: AdjustmentConfig['kind']): boolean {
  switch (kind) {
    case 'brightness-contrast':
    case 'color-balance':
    case 'curves':
    case 'hue-saturation':
    case 'levels':
    case 'shadows-highlights':
    case 'vibrance':
      return true;
    default:
      return false;
  }
}
